import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Eye } from 'lucide-react';
import { AreaAutocomplete } from '@/components/AreaSelector';
import JournalAutocomplete from '@/components/JournalAutocomplete';

const QUALIS_OPTIONS = ['A1', 'A2', 'A3', 'A4', 'B1', 'B2', 'B3', 'B4', 'C'];
const PUBLICATION_TYPES = [
  { value: 'revista', label: 'Revista Científica' },
  { value: 'congresso', label: 'Congresso/Evento' },
  { value: 'livro', label: 'Capítulo de Livro' },
  { value: 'edital', label: 'Edital de Pesquisa' }
];

const AdminEditCallPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    journal_name: '',
    qualis_classification: 'B1',
    publication_type: 'revista',
    deadline: '',
    continuous_flow: false,
    submission_url: '',
    is_free: true,
    issn: '',
    knowledge_areas: [],
    additional_info: '',
    status: 'active'
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Acesso negado');
      navigate('/dashboard');
      return;
    }
    fetchCall();
  }, [id, user]);

  const fetchCall = async () => {
    try {
      const response = await api.get(`/api/calls/${id}`);
      const call = response.data;
      
      // Format deadline for input type="date"
      let formattedDeadline = '';
      if (call.deadline) {
        formattedDeadline = call.deadline.split('T')[0];
      }
      
      setFormData({
        title: call.title || '',
        description: call.description || '',
        journal_name: call.journal_name || '',
        qualis_classification: call.qualis_classification || 'B1',
        publication_type: call.publication_type || 'revista',
        deadline: formattedDeadline,
        continuous_flow: call.continuous_flow || false,
        submission_url: call.submission_url || '',
        is_free: call.is_free !== false,
        issn: call.issn || '',
        knowledge_areas: call.knowledge_areas || [],
        additional_info: call.additional_info || '',
        status: call.status || 'active'
      });
    } catch (error) {
      toast.error('Erro ao carregar chamada');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        deadline: formData.deadline && !formData.continuous_flow 
          ? formData.deadline + 'T23:59:59Z' 
          : null
      };

      await api.put(`/api/calls/${id}`, submitData);
      toast.success('Chamada atualizada com sucesso!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar chamada');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta chamada? Esta ação não pode ser desfeita.')) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/api/calls/${id}`);
      toast.success('Chamada excluída com sucesso!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir chamada');
    } finally {
      setDeleting(false);
    }
  };

  const handleAreasChange = (newAreas) => {
    setFormData(prev => ({
      ...prev,
      knowledge_areas: newAreas
    }));
  };

  const handleJournalSelect = (journal) => {
    setFormData(prev => ({
      ...prev,
      journal_name: journal.name,
      issn: journal.issn || prev.issn,
      qualis_classification: journal.qualis || prev.qualis_classification
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <h1 className="text-xl font-bold">Editar Chamada</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => window.open(`/call/${id}`, '_blank')}
                className="gap-2"
              >
                <Eye className="h-4 w-4" />
                Visualizar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Informações Básicas</h2>
            
            <div className="space-y-4">
              <div>
                <Label>Título da Chamada *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Nome da Revista/Evento *</Label>
                <JournalAutocomplete
                  value={formData.journal_name}
                  onChange={(value) => setFormData({ ...formData, journal_name: value })}
                  onJournalSelect={handleJournalSelect}
                  placeholder="Digite para buscar revistas existentes..."
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Descrição *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={6}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Informações Adicionais</Label>
                <Textarea
                  value={formData.additional_info}
                  onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                  rows={3}
                  className="mt-2"
                  placeholder="Instruções para autores, formatação exigida, etc."
                />
              </div>
            </div>
          </div>

          {/* Classification */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Classificação</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>QUALIS/CAPES *</Label>
                <select
                  value={formData.qualis_classification}
                  onChange={(e) => setFormData({ ...formData, qualis_classification: e.target.value })}
                  className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2"
                >
                  {QUALIS_OPTIONS.map(q => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Tipo de Publicação *</Label>
                <select
                  value={formData.publication_type}
                  onChange={(e) => setFormData({ ...formData, publication_type: e.target.value })}
                  className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2"
                >
                  {PUBLICATION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>ISSN</Label>
                <Input
                  value={formData.issn}
                  onChange={(e) => setFormData({ ...formData, issn: e.target.value })}
                  placeholder="0000-0000"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2"
                >
                  <option value="active">Ativa</option>
                  <option value="archived">Arquivada</option>
                  <option value="draft">Rascunho</option>
                </select>
              </div>
            </div>
          </div>

          {/* Knowledge Areas */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Áreas do Conhecimento</h2>
            <AreaAutocomplete
              value={formData.knowledge_areas}
              onChange={handleAreasChange}
              label=""
              maxSelections={5}
              placeholder="Digite para buscar áreas específicas (ex: sociologia, química, direito...)"
            />
            <p className="text-xs text-gray-500 mt-2">
              Selecione áreas específicas do conhecimento para melhor correspondência com pesquisadores.
            </p>
          </div>

          {/* Deadline & Submission */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Prazo e Submissão</h2>
            
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.continuous_flow}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    continuous_flow: e.target.checked,
                    deadline: e.target.checked ? '' : formData.deadline
                  })}
                />
                <span className="text-sm font-semibold">Fluxo Contínuo (sem prazo definido)</span>
              </label>

              {!formData.continuous_flow && (
                <div>
                  <Label>Prazo (Deadline) *</Label>
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    required={!formData.continuous_flow}
                    className="mt-2 max-w-xs"
                  />
                </div>
              )}

              <div>
                <Label>URL de Submissão</Label>
                <Input
                  type="url"
                  value={formData.submission_url}
                  onChange={(e) => setFormData({ ...formData, submission_url: e.target.value })}
                  placeholder="https://revista.exemplo.com/submissao"
                  className="mt-2"
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_free}
                  onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                />
                <span className="text-sm font-semibold">Publicação Gratuita (sem taxas)</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#002FA7] hover:bg-[#001f75] gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminEditCallPage;
